import React, { useState } from 'react';
import FileUpload from './FileUpload';

/**
 * Shared Document Upload Form Component
 * Used in both modal (desktop) and inline (mobile) contexts
 */
const DocumentUploadForm = ({ 
  onSubmit, 
  onCancel, 
  uploading = false,
  stageId = null,
  actionId = null,
  compact = false // For inline mobile view
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'pdf',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }

    await onSubmit(selectedFile, { ...formData, stageId, actionId });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FileUpload
        onFileSelect={file => {
          setSelectedFile(file);
          if (!formData.name && file) {
            setFormData(prev => ({ ...prev, name: file.name }));
          }
        }}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        maxSize={10 * 1024 * 1024} // 10MB
        label="Select Document"
        description="PDF, Word, or Image files up to 10MB"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Employment Contract, Investigation Report"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type
        </label>
        <select
          value={formData.type}
          onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="pdf">PDF</option>
          <option value="doc">Word Document</option>
          <option value="email">Email</option>
          <option value="image">Image</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          rows={compact ? "2" : "3"}
          placeholder="Brief description of the document"
        />
      </div>

      <div className={`flex ${compact ? 'flex-col' : ''} space-x-3 ${compact ? 'space-x-0 space-y-2' : ''} pt-4`}>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          disabled={uploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={uploading || !selectedFile}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </button>
      </div>
    </form>
  );
};

export default DocumentUploadForm;
